import { createContext, useContext, ReactNode, useCallback, useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_CAMPAIGN } from '@/queries/campaigns';
import { UPDATE_CAMPAIGN, SET_STATUS_AS_SCHEDULED } from '@/mutations/campaigns';

import { CampaignStatus } from '@/gql/graphql';
import type {
  GetCampaignQuery,
  UpdateCampaignMutation,
  UpdateCampaignInput,
  SetStatusAsScheduledMutation
} from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';
import { CampaignScheduling } from '../../types';
import { Descendant } from 'slate';
type StepId = 'audience' | 'design' | 'review';

// Add interface for local variant state
interface LocalVariantState {
  variantName: string | undefined;
  content?: {
    editorState: Descendant[] | undefined;
  };
}

interface CampaignWizardState {
  campaignId: string;
  name: string;
  currentStep: StepId;
  campaign?: GetCampaignQuery['campaign'];
  loading: boolean;
  error?: Error;
  activeVariantId?: string;
  localVariants: Record<string, LocalVariantState | undefined>;
}

interface CampaignWizardContext extends CampaignWizardState {
  navigateToStep: (stepId: StepId) => void;
  handleNext: () => void;
  handleBack: () => void;
  updateSegments: (include: string[], exclude: string[]) => Promise<void>;
  isSaving: boolean;
  selectVariant: (variantId: string) => void;
  updateVariants: (variants: UpdateCampaignInput['variants']) => Promise<void>;
  updateScheduling: (scheduling: CampaignScheduling) => Promise<void>;
  updateLocalVariant: (variantId: string, updates: Partial<LocalVariantState>) => void;
}

const CampaignWizardContext = createContext<CampaignWizardContext | null>(null);

export function useCampaignWizard() {
  const context = useContext(CampaignWizardContext);
  if (!context) {
    throw new Error('useCampaignWizard must be used within a CampaignWizardProvider');
  }
  return context;
}

interface CampaignWizardProviderProps {
  children: ReactNode;
}

const steps: StepId[] = ['audience', 'design', 'review'];

export function CampaignWizardProvider({ children }: CampaignWizardProviderProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const campaignId = searchParams.get('id') ?? '';
  const name = searchParams.get('name') ?? '';

  // Get current step from the URL path
  const pathSegments = location.pathname.split('/');
  const currentStep = (pathSegments[pathSegments.length - 1] ?? 'audience') as StepId;
  const currentStepIndex = steps.indexOf(currentStep);
  const [activeVariantId, setActiveVariantId] = useState<string | undefined>(undefined);
  const [localVariants, setLocalVariants] = useState<Record<string, LocalVariantState>>({});
  // Fetch campaign data
  const { data, loading, error } = useQuery<GetCampaignQuery>(GET_CAMPAIGN, {
    variables: { id: campaignId },
    notifyOnNetworkStatusChange: true
  });

  const [setStatusAsScheduled, { loading: isScheduling }] =
    useMutation<SetStatusAsScheduledMutation>(SET_STATUS_AS_SCHEDULED, {
      onError: (error) => {
        toast({
          variant: 'destructive',
          title: 'Failed to schedule campaign',
          description: error.message || 'Please try again'
        });
      }
    });
  //Update campaign mutation - no auto refetch, just error handling
  const [updateCampaign, { loading: isSaving }] = useMutation<UpdateCampaignMutation>(
    UPDATE_CAMPAIGN,
    {
      refetchQueries: [{ query: GET_CAMPAIGN, variables: { id: campaignId } }],
      onError: (error) => {
        toast({
          variant: 'destructive',
          title: 'Failed to save changes',
          description: error.message || 'Please try again'
        });
      }
    }
  );

  const updateSegments = useCallback(
    async (include: string[], exclude: string[]) => {
      if (!campaignId) return;

      await updateCampaign({
        variables: { updateCampaignId: campaignId, input: { segments: { include, exclude } } }
      });
    },
    [campaignId, updateCampaign]
  );

  const navigateToStep = useCallback(
    (stepId: StepId) => {
      void navigate(`${stepId}?name=${encodeURIComponent(name)}&id=${campaignId}`);
    },
    [navigate, name, campaignId]
  );

  const handleNext = useCallback(() => {
    // If we're on the final step (review), update campaign status
    if (currentStep === 'review') {
      if (!campaignId || !data?.campaign.scheduled) return;

      const scheduled = data.campaign.scheduled as CampaignScheduling;
      const status: CampaignStatus =
        scheduled.type === 'draft' ? CampaignStatus.Draft : CampaignStatus.Scheduled;

      void (async () => {
        try {
          if (status === CampaignStatus.Scheduled) {
            await setStatusAsScheduled({ variables: { campaignId } });
          } else {
            await updateCampaign({
              variables: { updateCampaignId: campaignId, input: { status } }
            });
          }
          // Navigate back to campaigns list on success
          void navigate('..');
        } catch (_error) {
          // Error is handled by onError callback
          return;
        }
      })();
    } else if (currentStepIndex < steps.length - 1) {
      // Normal step navigation
      navigateToStep(steps[currentStepIndex + 1]);
    }
  }, [
    currentStep,
    currentStepIndex,
    campaignId,
    data?.campaign.scheduled,
    updateCampaign,
    navigate,
    navigateToStep
  ]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      navigateToStep(steps[currentStepIndex - 1]);
    } else {
      void navigate('..');
    }
  }, [currentStepIndex, navigateToStep, navigate]);

  // Initialize local state when campaign loads
  useEffect(() => {
    if (!data?.campaign.variants.length) return;

    // Initialize local variants from server data
    const initialLocalState = data.campaign.variants.reduce<Record<string, LocalVariantState>>(
      (acc, variant) => ({
        ...acc,
        [variant.variantId]: {
          variantName: variant.variantName,
          content: variant.content as LocalVariantState['content']
        }
      }),
      {}
    );

    setLocalVariants(initialLocalState);

    // Set initial active variant if none selected
    if (!activeVariantId) {
      setActiveVariantId(data.campaign.variants[0].variantId);
    }
  }, [data?.campaign.variants, activeVariantId]);

  const selectVariant = useCallback((variantId: string) => {
    setActiveVariantId(variantId);
  }, []);

  const updateVariants = useCallback(
    async (variants: UpdateCampaignInput['variants']) => {
      if (!campaignId) return;
      try {
        await updateCampaign({ variables: { updateCampaignId: campaignId, input: { variants } } });
      } catch (_error) {
        // Error is handled by onError callback
      }
    },
    [campaignId, updateCampaign]
  );

  const updateScheduling = useCallback(
    async (scheduling: CampaignScheduling | undefined) => {
      if (!campaignId) return;

      try {
        // Correctly call updateCampaign once with the proper variables structure
        await updateCampaign({
          variables: { updateCampaignId: campaignId, input: { scheduled: scheduling } }
        });
      } catch (_error) {
        // Error is handled by the mutation's onError callback
        // No need to re-throw unless specific handling is needed here
      }
    },
    [campaignId, updateCampaign]
  );

  const updateLocalVariant = useCallback(
    (variantId: string, updates: Partial<LocalVariantState>) => {
      setLocalVariants((current) => ({
        ...current,
        [variantId]: {
          ...current[variantId],
          ...updates
        }
      }));
    },
    []
  );

  const value: CampaignWizardContext = {
    campaignId,
    name,
    currentStep,
    campaign: data?.campaign,
    loading,
    error: error as Error | undefined,
    isSaving: isScheduling || isSaving,
    navigateToStep,
    handleNext,
    handleBack,
    updateSegments,
    activeVariantId,
    selectVariant,
    updateVariants,
    updateScheduling,
    localVariants,
    updateLocalVariant
  };

  return <CampaignWizardContext.Provider value={value}>{children}</CampaignWizardContext.Provider>;
}
